import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Plus, Edit2, Trash2, X, Loader2, Upload, ExternalLink, User as UserIcon } from "lucide-react";

export default function AdminGovernance() {
    const [members, setMembers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = () => apiPrivate.get("/admin/governance").then(r => setMembers(r.data));
    useEffect(() => { load(); }, []);

    const remove = async (m) => {
        if (!window.confirm(`Eliminare il profilo di ${m.nome}?`)) return;
        await apiPrivate.delete(`/admin/governance/${m.id}`);
        load();
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-governance">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Governance</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">Membri del <span className="font-serif-italic text-gradient-tropical">CDA</span>.</h1>
                    </div>
                    <button onClick={() => setCreating(true)} className="btn-rebel" data-testid="btn-new-gov"><Plus className="h-4 w-4" /> Nuovo membro</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="gov-grid">
                    {members.length === 0 && <div className="bento p-10 text-center text-zinc-500 col-span-full" data-testid="gov-empty">Nessun membro. Aggiungine uno per popolare la sezione Governance del sito.</div>}
                    {members.map(m => (
                        <div key={m.id} className="bento p-0 overflow-hidden" data-testid={`gov-card-${m.id}`}>
                            <div className="aspect-[4/3] bg-tropical-soft relative grain">
                                {m.photo_url && <img src={m.photo_url + "?v=" + (m.created_at || "")} alt={m.nome} className="w-full h-full object-cover" />}
                                {!m.published && <span className="absolute top-3 left-3 bg-black/70 text-magenta text-[10px] font-bold px-2 py-1 font-grotesk">BOZZA</span>}
                            </div>
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-display text-xl">{m.nome}</div>
                                        <div className="text-zinc-400 text-sm mt-1">{m.titolo}</div>
                                        <a href={`/governance/${m.slug}`} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest font-grotesk text-acid inline-flex items-center gap-1 mt-3" data-testid={`gov-view-${m.id}`}>
                                            /governance/{m.slug} <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <button onClick={() => setEditing(m)} className="p-2 border border-white/10 hover:border-acid hover:text-acid transition rounded-full" data-testid={`gov-edit-${m.id}`}>
                                            <Edit2 className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => remove(m)} className="p-2 border border-white/10 hover:border-magenta hover:text-magenta transition rounded-full" data-testid={`gov-del-${m.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {(editing || creating) && (
                    <GovernanceModal
                        initial={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { setEditing(null); setCreating(false); load(); }}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function GovernanceModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || { nome: "", titolo: "", bio: "", linkedin: "", email: "", order: 0, published: true });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(initial?.photo_url || null);

    const submit = async () => {
        setSaving(true); setErr(null);
        try {
            let saved;
            if (initial) {
                const r = await apiPrivate.patch(`/admin/governance/${initial.id}`, form);
                saved = r.data;
            } else {
                const r = await apiPrivate.post("/admin/governance", form);
                saved = r.data;
                initial = saved; // so subsequent photo upload knows the ID
            }
            onSaved();
        } catch (e) { setErr(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!initial?.id) {
            setErr("Salva prima i dati base, poi potrai caricare la foto.");
            return;
        }
        setUploading(true); setErr(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await apiPrivate.post(`/admin/governance/${initial.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setPhotoUrl(r.data.photo_url + "?v=" + Date.now());
        } catch (e) { setErr(e?.response?.data?.detail || "Errore upload"); }
        finally { setUploading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="gov-modal">
            <div className="bg-[#0a0c1c] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <span className="label-tag">— {initial ? "Modifica" : "Nuovo"} membro</span>
                            <h2 className="font-display text-3xl tracking-tighter mt-2">Profilo governance.</h2>
                        </div>
                        <button onClick={onClose} data-testid="gov-modal-close"><X className="h-5 w-5 text-zinc-500" /></button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                        <div className="md:col-span-1">
                            <div className="label-tag mb-2">Foto profilo</div>
                            <div className="aspect-[4/5] bg-tropical-soft rounded-2xl overflow-hidden mb-3 grain">
                                {photoUrl ? <img src={photoUrl} alt="profile" className="w-full h-full object-cover" /> :
                                    <div className="w-full h-full flex items-center justify-center"><UserIcon className="h-20 w-20 text-white/40" /></div>}
                            </div>
                            <label htmlFor="gov-photo-input" className="block text-center cursor-pointer text-xs uppercase tracking-widest font-grotesk px-3 py-3 border border-white/10 hover:border-acid hover:text-acid rounded-full transition" data-testid="gov-photo-upload">
                                {uploading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : <><Upload className="h-3 w-3 inline mr-2" />Carica foto</>}
                            </label>
                            <input id="gov-photo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="gov-photo-input-file" />
                            {!initial?.id && <p className="text-[10px] text-zinc-600 mt-2 font-grotesk text-center">Salva prima i dati per abilitare upload foto</p>}
                        </div>

                        <div className="md:col-span-2 space-y-4">
                            <Field label="Nome completo *" v={form.nome} on={v => setForm(s => ({ ...s, nome: v }))} testid="gov-nome" />
                            <Field label="Titolo / Ruolo *" v={form.titolo} on={v => setForm(s => ({ ...s, titolo: v }))} testid="gov-titolo" />
                            <Field label="Bio (puoi usare paragrafi multipli)" v={form.bio} on={v => setForm(s => ({ ...s, bio: v }))} multiline rows={6} testid="gov-bio" />
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="LinkedIn URL" v={form.linkedin} on={v => setForm(s => ({ ...s, linkedin: v }))} testid="gov-linkedin" />
                                <Field label="Email" v={form.email} on={v => setForm(s => ({ ...s, email: v }))} testid="gov-email" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="Slug URL" v={form.slug} on={v => setForm(s => ({ ...s, slug: v }))} testid="gov-slug" placeholder="auto da nome" />
                                <Field label="Ordine" type="number" v={form.order} on={v => setForm(s => ({ ...s, order: parseInt(v) || 0 }))} testid="gov-order" />
                                <div>
                                    <div className="label-tag mb-2">Stato</div>
                                    <select value={form.published ? "1" : "0"} onChange={(e) => setForm(s => ({ ...s, published: e.target.value === "1" }))} className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none" data-testid="gov-published">
                                        <option value="1">Pubblicato</option>
                                        <option value="0">Bozza</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {err && <div className="mt-4 border border-magenta/40 bg-magenta/10 text-magenta px-3 py-2 text-sm rounded-2xl" data-testid="gov-err">{err}</div>}

                    <div className="flex gap-3 mt-8">
                        <button onClick={submit} disabled={saving || !form.nome || !form.titolo} className="btn-rebel flex-1 justify-center" data-testid="gov-save">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (initial ? "Salva modifiche" : "Crea profilo")}
                        </button>
                        <button onClick={onClose} className="btn-ghost" data-testid="gov-cancel">Annulla</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, on, multiline, rows = 3, type = "text", testid, placeholder }) {
    return (
        <div>
            <div className="label-tag mb-2">{label}</div>
            {multiline ? (
                <textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full bg-[#0F0F0F] border border-white/10 text-white p-3 font-grotesk focus:outline-none focus:border-acid resize-none" />
            ) : (
                <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-acid" />
            )}
        </div>
    );
}
